/**
 * AdminTopNav · v2.7.41 — DELEGATES to AdminLayoutShell
 * ────────────────────────────────────────────────────────────────────
 * The legacy two-tier top-bar nav was replaced by the hybrid layout
 * shell that supports drag-and-drop docking between a 280-px left
 * sidebar and the top horizontal navbar (per Feb-2026 sidebar
 * restoration spec). This thin wrapper preserves the existing import
 * surface (`@/components/admin/AdminTopNav`) and prop contract so the
 * AdminDashboardPage call site doesn't need to change.
 *
 * Prior render lived here in v2.7.14 - v2.7.40; restored history is
 * available via `git log`.
 */
import { useEffect, useState } from "react";
import AdminLayoutShell from "@/components/admin/AdminLayoutShell";
import useAdminPresence from "@/hooks/useAdminPresence";

export default function AdminTopNav({
  activeTab,
  onTopNav,
  adminRole,
  adminRoles = [],
  subsEnabled = true,
  unreadBadges = {},
}) {
  // Live numeric badges (NWS Forecast Discussions / Active Warnings /
  // State Emergencies / Active Power Outages) — refreshed every 10 s.
  const [navBadges, setNavBadges] = useState({});
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const adminApi = (await import("@/lib/adminApi")).default;
        const { data } = await adminApi.get("/admin/weather/badges");
        if (!cancelled) setNavBadges((prev) => ({ ...prev, ...(data || {}) }));
      } catch { /* silent — non-admin tokens land here */ }
    };
    load();
    const id = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // v2.7.63+E46.6 · Live Delivery Queue counter — polled every 15 s.
  // Merges into navBadges under `deliveryQueue` so the nav item's badge
  // rendering path is unified. Non-privileged tokens will 403 silently
  // and the badge simply stays at 0.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const adminApi = (await import("@/lib/adminApi")).default;
        const { data } = await adminApi.get("/deliveries/admin/queue-count");
        if (!cancelled) {
          setNavBadges((prev) => ({ ...prev, deliveryQueue: data?.total || 0 }));
        }
      } catch { /* silent — non-privileged tokens */ }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // v2.7.45+E4 · Corporate Email accounts + sidebar unread-count badges.
  // - Accounts list refreshed every 5 min (changes rarely; updates also
  //   broadcast via a window event on add/edit/delete from the settings
  //   panel so the sidebar reflects immediately).
  // - Unread counts polled every 60 s — single batched IMAP call on the
  //   backend (one connection per parent mailbox, alias UNSEEN searches
  //   share the connection) so polling cost stays flat regardless of how
  //   many aliases the user has configured.
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailUnread, setEmailUnread] = useState({});
  useEffect(() => {
    let cancelled = false;
    const loadAccounts = async () => {
      try {
        const adminApi = (await import("@/lib/adminApi")).default;
        const { data } = await adminApi.get("/admin/email/accounts");
        if (!cancelled) setEmailAccounts(data?.items || []);
      } catch { /* silent — non-admin tokens or no email accounts */ }
    };
    loadAccounts();
    const onMutated = () => loadAccounts();
    window.addEventListener("rtg-email-accounts-changed", onMutated);
    const id = setInterval(loadAccounts, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.removeEventListener("rtg-email-accounts-changed", onMutated);
      clearInterval(id);
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const loadUnread = async () => {
      try {
        const adminApi = (await import("@/lib/adminApi")).default;
        const { data } = await adminApi.get("/admin/email/unread-counts");
        if (!cancelled) setEmailUnread(data?.counts || {});
      } catch { /* silent — IMAP issues or no accounts */ }
    };
    loadUnread();
    const onMarkRead = () => loadUnread();
    window.addEventListener("rtg-email-mark-read", onMarkRead);
    const id = setInterval(loadUnread, 60000);
    return () => {
      cancelled = true;
      window.removeEventListener("rtg-email-mark-read", onMarkRead);
      clearInterval(id);
    };
  }, []);

  // Track the current `#sub=…` so the shell can highlight the active row
  const readSub = () => {
    if (typeof window === "undefined") return null;
    const h = (window.location.hash || "").replace(/^#/, "");
    return new URLSearchParams(h).get("sub");
  };
  const [activeSub, setActiveSub] = useState(readSub());
  useEffect(() => {
    const onHash = () => setActiveSub(readSub());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Site-online status — derive from presence availability (same heuristic
  // as the legacy top bar).
  const presence = useAdminPresence();
  const dispatchHealthy = Object.keys(presence || {}).length >= 0;

  return (
    <AdminLayoutShell
      activeTab={activeTab}
      activeSub={activeSub}
      onTopNav={onTopNav}
      adminRole={adminRole}
      adminRoles={adminRoles}
      subsEnabled={subsEnabled}
      unreadBadges={unreadBadges}
      dispatchHealthy={dispatchHealthy}
      badges={navBadges}
      emailAccounts={emailAccounts}
      emailUnread={emailUnread}
    />
  );
}
